import React, { useEffect, useRef, useState } from "react";

// Premium animated speedometer for the Growth Score.
// 240° arc opening at the bottom. Needle geometry is computed directly from a
// single rAF-animated angle (no CSS transform-origin), so the pin always points
// correctly and stays in sync with the arc fill and the number.
export default function Speedometer({
  value = 74,
  max = 100,
  size = 360,
  label = "Growth Score",
  delta,
  duration = 2600,
  dark = false,
  cycle,
  pause = 1600,
}) {
  const P = dark
    ? { track: "#2E3326", tickMajor: "#7C8270", tickMinor: "#484D3D", needle: "#FFFFFF", num: "#FFFFFF", sub: "rgba(255,255,255,.5)", label: "rgba(255,255,255,.62)", chipBg: "rgba(182,225,79,.16)", chipFg: "#B6E14F" }
    : { track: "#ECEEE4", tickMajor: "#B9BAB3", tickMinor: "#D6D7D0", needle: "#16180F", num: "#16180F", sub: "#9A9C90", label: "#8A8C82", chipBg: "rgba(147,192,32,.12)", chipFg: "#5F8213" };
  const W = size;
  const H = size * 0.7;
  const cx = W / 2;
  const cy = H * 0.62;
  const r = size * 0.37;
  const stroke = Math.max(10, size * 0.045);

  const START = -120; // degrees, 0 = up, + = clockwise
  const SWEEP = 240;
  const [idx, setIdx] = useState(0);
  const cycling = Array.isArray(cycle) && cycle.length > 1;
  useEffect(() => {
    if (!cycling) return;
    const iv = setInterval(() => setIdx((i) => (i + 1) % cycle.length), duration + pause);
    return () => clearInterval(iv);
  }, [cycling, cycle, duration, pause]);

  const activeValue = cycling ? cycle[idx] : value;
  const prevValue = cycling ? cycle[(idx - 1 + cycle.length) % cycle.length] : value;
  const activeDelta = cycling ? activeValue - prevValue : delta;

  const frac = Math.max(0, Math.min(1, activeValue / max));
  const target = START + frac * SWEEP;

  const [ang, setAng] = useState(START);
  const angRef = useRef(START);
  const raf = useRef();
  useEffect(() => {
    const from = angRef.current;
    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const a = from + (target - from) * ease(p);
      angRef.current = a;
      setAng(a);
      if (p < 1) raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, [target, duration]);

  const shown = Math.round(((ang - START) / SWEEP) * max);

  const pt = (a, rr) => {
    const rad = (a * Math.PI) / 180;
    return { x: cx + rr * Math.sin(rad), y: cy - rr * Math.cos(rad) };
  };
  const arcPath = (a0, a1, rr) => {
    const p0 = pt(a0, rr);
    const p1 = pt(a1, rr);
    const large = Math.abs(a1 - a0) > 180 ? 1 : 0;
    return `M ${p0.x.toFixed(2)} ${p0.y.toFixed(2)} A ${rr} ${rr} 0 ${large} 1 ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`;
  };

  const zones = [
    { from: 0, to: 0.55, c: "#EAD3D3" },
    { from: 0.55, to: 0.7, c: "#EFE0C5" },
    { from: 0.7, to: 1, c: "#D8E6B0" },
  ];
  const ticks = Array.from({ length: 11 }, (_, i) => i);

  // Needle geometry, computed live from the animated angle.
  const needleLen = r - stroke * 0.55;
  const baseW = size * 0.022;
  const tailLen = size * 0.06;
  const tip = pt(ang, needleLen);
  const baseR = pt(ang - 90, baseW);
  const baseL = pt(ang + 90, baseW);
  const tail = pt(ang + 180, tailLen);
  const needlePoints = `${tip.x.toFixed(2)},${tip.y.toFixed(2)} ${baseR.x.toFixed(2)},${baseR.y.toFixed(2)} ${tail.x.toFixed(2)},${tail.y.toFixed(2)} ${baseL.x.toFixed(2)},${baseL.y.toFixed(2)}`;

  return (
    <div className="relative select-none" style={{ width: W, height: H }}>
      <svg width={W} height={H} className="overflow-visible">
        <defs>
          <linearGradient id="spdGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#B6E14F" />
            <stop offset="55%" stopColor="#93C020" />
            <stop offset="100%" stopColor="#6FA015" />
          </linearGradient>
          <filter id="spdGlow" x="-40%" y="-40%" width="180%" height="180%">
            <feDropShadow dx="0" dy="0" stdDeviation={size * 0.022} floodColor="#B6E14F" floodOpacity="0.6" />
          </filter>
        </defs>

        {/* Track */}
        <path d={arcPath(START, START + SWEEP, r)} fill="none" stroke={P.track} strokeWidth={stroke} strokeLinecap="round" />
        {/* Subtle zones */}
        {!dark && zones.map((z, i) => (
          <path key={i} d={arcPath(START + z.from * SWEEP, START + z.to * SWEEP, r)} fill="none" stroke={z.c} strokeWidth={stroke * 0.5} strokeLinecap="butt" opacity={0.9} />
        ))}
        {/* Progress fill (in sync with needle) */}
        {ang > START + 0.4 && (
          <path d={arcPath(START, ang, r)} fill="none" stroke={activeValue >= 55 ? "url(#spdGrad)" : "#D95C5C"} strokeWidth={stroke} strokeLinecap="round" filter={dark && activeValue >= 55 ? "url(#spdGlow)" : undefined} />
        )}
        {/* Ticks */}
        {ticks.map((i) => {
          const a = START + (i / 10) * SWEEP;
          const major = i % 5 === 0;
          const o = pt(a, r - stroke * 0.75);
          const inn = pt(a, r - stroke * (major ? 1.5 : 1.15));
          return <line key={i} x1={inn.x} y1={inn.y} x2={o.x} y2={o.y} stroke={major ? P.tickMajor : P.tickMinor} strokeWidth={major ? 2 : 1} strokeLinecap="round" />;
        })}
        {/* Needle */}
        <polygon points={needlePoints} fill={P.needle} />
        <circle cx={cx} cy={cy} r={size * 0.036} fill={P.needle} />
        <circle cx={cx} cy={cy} r={size * 0.015} fill="#B6E14F" />
      </svg>

      {/* Value overlay — seated in the lower gap, below the hub */}
      <div className="absolute inset-x-0 flex flex-col items-center" style={{ top: cy + size * 0.055 }}>
        <div className="flex items-baseline justify-center leading-none">
          <span className="font-extrabold tracking-tighter" style={{ fontSize: size * 0.15, color: P.num }}>{shown}</span>
          <span className="font-semibold" style={{ fontSize: size * 0.05, marginLeft: 3, color: P.sub }}>/ {max}</span>
        </div>
        <div className="font-bold uppercase" style={{ fontSize: size * 0.03, marginTop: size * 0.012, letterSpacing: "0.12em", color: P.label }}>{label}</div>
        {activeDelta != null && (
          <div className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-bold transition-colors duration-500" style={{ marginTop: size * 0.022, fontSize: size * 0.033, background: activeDelta >= 0 ? P.chipBg : (dark ? "rgba(217,92,92,.18)" : "rgba(217,92,92,.12)"), color: activeDelta >= 0 ? P.chipFg : (dark ? "#F0A3A3" : "#C0473F") }}>
            {activeDelta >= 0 ? "+" : ""}{activeDelta} this month {activeDelta >= 0 ? "↑" : "↓"}
          </div>
        )}
      </div>
    </div>
  );
}
